import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LabelId, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { deleteLocalSnapshots, planLocalDeletion } from './localDraftDeletion';
import { useDeleteDraft } from './useDeleteDraft';
import {
  useMessageDialogStore,
  useUserCommunicationsStore,
} from '../../../store';
import { partitionSelection } from '../../../utils/localDraftSelection';
import { resolveDraftCommunication } from '../../../utils/userCommunicationHelpers';
import { useCustomToast } from '../../useCustomToast';

export class BulkDeleteDraftsError extends Error {
  readonly failed: number;
  readonly total: number;

  constructor(failed: number, total: number) {
    super(`Failed to delete ${failed} of ${total} drafts`);
    this.name = 'BulkDeleteDraftsError';
    this.failed = failed;
    this.total = total;
  }
}

const LOG_MESSAGES = {
  LOCAL_DELETE_FAILED: 'Deleting local composes failed',
} as const;

interface DraftToDelete {
  draftId: string;
  communication?: UserCommunication;
}

export interface BulkDeleteDraftsOptions {
  silent?: boolean;
}

export const useBulkDeleteDrafts = () => {
  const { t } = useTranslation();
  const { deleteDraft } = useDeleteDraft();

  const { setDeleteLoading, getUserCommunicationByMessageId } =
    useUserCommunicationsStore();
  const { errorMessage, promiseToast } = useCustomToast();

  const handleBulkDeleteDrafts = useCallback(
    async (
      messageIds: string[] | string,
      onSuccess?: () => void,
      { silent = false }: BulkDeleteDraftsOptions = {},
    ) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) return;

      const { serverIds, localSessionIds } =
        partitionSelection(messageIdsArray);
      const localPlan = planLocalDeletion(localSessionIds);

      const resolvedDrafts = serverIds
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);

          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }

          if (!userCommunication.labelIds?.includes(LabelId.DRAFT)) {
            log.warn(
              `UserCommunication with messageId ${messageId} is not a draft`,
            );
            return null;
          }

          const resolvedDraftId =
            resolveDraftCommunication(userCommunication)?.draftId;

          if (!resolvedDraftId) {
            log.error(
              `UserCommunication with messageId ${messageId} has no draftId`,
            );
            return null;
          }

          return {
            draftId: resolvedDraftId,
            communication: userCommunication,
          };
        })
        .filter(
          (
            item,
          ): item is { draftId: string; communication: UserCommunication } =>
            item !== null,
        );

      const draftsToDelete: DraftToDelete[] = [
        ...resolvedDrafts,
        ...localPlan.serverDraftIds.map((draftId) => ({ draftId })),
      ];
      const localCount = localPlan.localSessionIds.length;

      if (draftsToDelete.length === 0 && localCount === 0) {
        if (localPlan.savingSessionIds.length > 0 && serverIds.length === 0) {
          return;
        }
        if (silent) {
          throw new BulkDeleteDraftsError(
            messageIdsArray.length,
            messageIdsArray.length,
          );
        }
        errorMessage(t('inbox.userCommunication.actions.deleteDraftError'));
        return;
      }

      const closeIfShowingDraftPromise = useMessageDialogStore
        .getState()
        .closeIfShowingDraft(draftsToDelete.map(({ draftId }) => draftId));

      onSuccess?.();
      setDeleteLoading(true);

      const localFailureCount = deleteLocalSnapshots(
        localPlan.localSessionIds,
      ).then(
        () => 0,
        (error) => {
          log.error(LOG_MESSAGES.LOCAL_DELETE_FAILED, error);
          return localCount;
        },
      );
      const total = draftsToDelete.length + localCount;
      const loadingMessage =
        total === 1
          ? t('inbox.userCommunication.actions.deletingDraft')
          : t('inbox.userCommunication.actions.deletingDrafts', {
              count: total,
            });
      const successMessageText =
        total === 1
          ? t('inbox.userCommunication.actions.deleteDraftSuccess')
          : t('inbox.userCommunication.actions.deleteDraftMultipleSuccess', {
              count: total,
            });

      const runDeletes = async (): Promise<void> => {
        try {
          await closeIfShowingDraftPromise;
          const localFailures = await localFailureCount;
          const results = await Promise.all(
            draftsToDelete.map(async ({ draftId, communication }) => {
              try {
                const success = await deleteDraft(draftId, {
                  removeFromStore: true,
                });

                if (!success) {
                  throw new Error('Failed to delete draft on the platform');
                }

                return { draftId, success: true, communication };
              } catch (error) {
                log.error(
                  `Failed to delete draft with draftId ${draftId}:`,
                  error,
                );
                return { draftId, success: false, communication, error };
              }
            }),
          );

          const failed = results.filter((r) => !r.success);

          const failedCount = failed.length + localFailures;

          if (failedCount > 0) {
            log.warn(
              `${failedCount} failures while deleting drafts out of ${total} operations`,
            );

            throw new BulkDeleteDraftsError(failedCount, total);
          }
        } finally {
          setDeleteLoading(false);
        }
      };

      const deletePromise = runDeletes();

      const errorText = (error: unknown) =>
        error instanceof BulkDeleteDraftsError && error.failed < error.total
          ? t('inbox.userCommunication.actions.partialDeleteDraftError', {
              failed: error.failed,
              total: error.total,
            })
          : t('inbox.userCommunication.actions.deleteDraftError');

      if (!silent) {
        promiseToast(
          deletePromise,
          loadingMessage,
          successMessageText,
          errorText,
        );
      }

      try {
        await deletePromise;
      } catch (error) {
        log.error('Error while bulk deleting drafts:', error);
        if (silent) {
          throw error;
        }
      }
    },
    [deleteDraft, setDeleteLoading, promiseToast, errorMessage, t],
  );

  return { handleBulkDeleteDrafts };
};
